import {
    Component,
    computed,
    EventEmitter,
    model,
    OnDestroy,
    OnInit,
    Output,
    signal,
    ViewChild
} from '@angular/core';
import {AuthService} from '../../../../service/services/auth/auth.service';
import {Const, Theme} from '../../../helpers/Const';
import {ThemeService} from '../../../../service/services/misc/theme/theme.service';
import {NavigationService} from '../../../../service/services/misc/navigation/navigation.service';
import {DialogService} from '../../../../service/services/misc/dialog/dialog.service';
import {SupportDialogComponent} from '../../../dialog/support-dialog/support-dialog.component';
import {SwaService} from '../../../../service/services/misc/swa/swa.service';
import {PopoverComponent} from '@fundamental-ngx/core/popover';
import {IconProp} from "@fortawesome/fontawesome-svg-core";
import {ThemeSubjectModel} from "../../../../service/services/misc/theme/theme-subject.model";
import {Subscription} from "rxjs";
import {UisService} from "../../../../service/services/misc/uis/uis.service";
import {UserInfoDialogComponent} from "../../../dialog/user-info/user-info.component";
import {Utils} from "../../../helpers/Utils";
import {FullscreenService} from "../fullscreen/fullscreen.service";
import {AiAssistantService} from "../../../../service/services/misc/ai-assistant/ai-assistant.service";

@Component({
    selector: 'app-header',
    templateUrl: './header.component.html',
    styleUrls: ['./header.component.scss'],
    standalone: false
})
export class HeaderComponent implements OnInit, OnDestroy{
    Theme = Theme;
    Utils = Utils;
    theme$ = this._themeService.themeSubject.asObservable();
    username = this._authService.getAuthenticatedUsername();
    condensed = model<boolean>();
    @Output() toggleCondensed = new EventEmitter<boolean>();

    @ViewChild('helpPopover')
        helpPopover: PopoverComponent;

    @ViewChild('userPopover')
        userPopover: PopoverComponent;
    Const = Const;

    themeSubscription: Subscription;
    darkIcon = signal<IconProp>(undefined);
    lightIcon =  signal<IconProp>(undefined);
    hcbIcon = signal<IconProp>(undefined);
    isUserPopoverOpen = false;
    isHelpPopoverOpen = false;

    condenseButtonTitle = computed(() => !this.condensed() ? 'Collapse Panel' : 'Expand Panel');
    condenseWrapperClass = computed(() => this.condensed() ? 'nav-collapsed' : 'nav-expanded');

    constructor(
        private _authService: AuthService,
        private _navigationService: NavigationService,
        protected _dialogService: DialogService,
        private _themeService: ThemeService,
        private _swaService: SwaService,
        private _uisService: UisService,
        private _fullScreenService : FullscreenService,
        protected aiAssistantService: AiAssistantService
    ) {
    }

    ngOnInit() {
        this.themeSubscription = this.theme$.subscribe(this.adjustThemeIcon.bind(this));
    }

    adjustThemeIcon(theme: ThemeSubjectModel) {
        switch (theme.selectedTheme) {
        case Theme.ATOM_DARK:
            this.darkIcon.set(['workspace' as any, 'moonFull' as any]);
            this.lightIcon.set(['workspace' as any, 'sunEmpty' as any]);
            this.hcbIcon.set(['workspace' as any, 'hcbEmpty' as any]);
            break;

        case Theme.ATOM_LIGHT:
            this.darkIcon.set(['workspace' as any, 'moonEmpty' as any]);
            this.lightIcon.set(['workspace' as any, 'sunFull' as any]);
            this.hcbIcon.set(['workspace' as any, 'hcbEmpty' as any]);
            break;

        default:
            this.darkIcon.set(['workspace' as any, 'moonEmpty' as any]);
            this.lightIcon.set(['workspace' as any, 'sunEmpty' as any]);
            this.hcbIcon.set(['workspace' as any, 'hcbFull' as any]);
            break;
        }
    }

    ngOnDestroy() {
        if (this.themeSubscription) {
            this.themeSubscription.unsubscribe();
        }
    }

    onHelpClick() {
        this._swaService.sendEvent(this._swaService.SWA_EVENT_TYPES.OPEN_UNIFIED_SERVICES_DOCS);
        this._navigationService.openInNewTab(Const.RM_DOCS_URL);
        this.helpPopover.close();
    }

    onCliClick() {
        this._swaService.sendEvent(this._swaService.SWA_EVENT_TYPES.OPEN_ATOMCTL_CLI_DOCS);
        this._navigationService.openInNewTab(Const.CLI_DOCS_URL);
        this.helpPopover.close();
    }

    onWorkspaceHelpClick() {
	    this._swaService.sendEvent(this._swaService.SWA_EVENT_TYPES.OPEN_WORKSPACE_DOCS);
	    this._navigationService.openInNewTab(Const.RM_DOCS_WORKSPACE);
	    this.helpPopover.close();
    }

    changeTheme(theme: string) {
        this._uisService.sendUISEvent(this._uisService.UIS_EVENT_TYPES.THEME_CHANGE + theme, {theme: theme});
	    this._swaService.sendCustomEvent(this._swaService.SWA_EVENT_TYPES.THEME_CHANGE, [theme], []);
	    this._themeService.setTheme(theme);
    }

    logout() {
        this._uisService.sendEvent(this._uisService.UIS_EVENT_TYPES.HEADER_LOGOUT_CLICKED);
        this._swaService.sendEvent(this._swaService.SWA_EVENT_TYPES.HEADER_LOGOUT_CLICKED);
        this.authLogout();
    }

    openUserInfoDialog() {
        this._uisService.sendEvent(this._uisService.UIS_EVENT_TYPES.HEADER_ABOUT_CLICKED);
        this._swaService.sendEvent(this._swaService.SWA_EVENT_TYPES.HEADER_ABOUT_CLICKED);
        this._dialogService.openDialog(UserInfoDialogComponent, {});
        this.userPopover.close();
    }

    openSupportDialog() {
        this._uisService.sendEvent(this._uisService.UIS_EVENT_TYPES.HEADER_SUPPORT_CLICKED);
        this._swaService.sendEvent(this._swaService.SWA_EVENT_TYPES.HEADER_SUPPORT_CLICKED);
        this._dialogService.openDialog(SupportDialogComponent, {});
        this.helpPopover.close();
    }

    authLogout() {
        this._authService.logout();
    }

    onToggleCondensed() {
        this.toggleCondensed.emit();
        this.condensed.set(!this.condensed());
        this._fullScreenService.emitIsExpanded(this.condensed());
    }

    /**
     * Shell-bar AI toggle. When opening while viewing a specific location (not the
     * org-list root), seed the widget with hidden location context so the assistant
     * knows where the user is; otherwise just open/close with no context.
     */
    onToggleAiAssistant() {
        if (this.aiAssistantService.isOpen) {
            this.aiAssistantService.close();
            return;
        }
        const context = this._buildLocationContextFromUrl();
        if (context) {
            this.aiAssistantService.open(context);
        } else {
            this.aiAssistantService.clearContext();
            this.aiAssistantService.open();
        }
    }

    /**
     * Parse the current router URL into a hidden location context, or null when at
     * the organizations root (nothing specific to anchor on).
     *
     * URL grammar: /project/:project/organizations[/:org]?path=/folder/subfolder
     * The folder/resource-group tail lives in the `path` query param (set by
     * FolderComponent), NOT in the URL path segments. Full path = org + path param,
     * mirroring FolderComponent `_fullPath`.
     */
    private _buildLocationContextFromUrl() {
        const rawUrl = this._navigationService.getRouter().url || '';
        const [pathPart, queryPart = ''] = rawUrl.split('?');
        const segments = pathPart.split('/').filter(Boolean);
        const orgsIndex = segments.indexOf('organizations');
        // No org segment after "organizations" → still at the root list.
        if (orgsIndex < 0 || !segments[orgsIndex + 1]) {
            return null;
        }
        const view = segments[orgsIndex + 2] || '';
        const query = new URLSearchParams(queryPart);
        const project = segments[1] === Const.ALL_PROJECTS_PARAM ? '' : decodeURIComponent(segments[1] || '');
        const org = decodeURIComponent(segments[orgsIndex + 1]);
        const subPath = query.get('path') || '';
        const fullPath = `${org}${subPath}`;
        let resourceType = 'Organization';
        if (view === 'resource-groups') {
            resourceType = 'Resource Group';
        } else if (subPath) {
            resourceType = 'Folder';
        }
        return this.aiAssistantService.buildLocationContext(resourceType, fullPath, project);
    }

    onClickDownload() {
        this._swaService.sendEvent(this._swaService.SWA_EVENT_TYPES.CLI_DOWNLOAD_CLICKED);
    }
}
